/* ============================================================================
   C | Custom Header – behavior
   ----------------------------------------------------------------------------
   Two custom elements:
     <sticky-header>  – outer wrapper, copies the original theme behavior:
                         IntersectionObserver to add `in-view`, and a scroll
                         observer that toggles scrolled-past-header /
                         shopify-section-header-sticky / -hidden classes
                         (this drives the original CSS in base.css that we
                         re-use, so the visual states match the original).
     <c-header>       – inner wrapper handling mega-menu hover and the
                         mobile drawer.
   The <sticky-header> definition is guarded so it doesn't conflict if the
   original header section is still enabled in the theme.

   FIX (Mega-Menu): closeMega() setzt pointer-events sofort auf 'none'
        (Inline-Style), damit ein gerade schliessendes Panel den Hover des
        anderen Panels nicht mehr blockiert. Hidden wird wie gehabt nach
        transitionend gesetzt, damit der Fade-Out sichtbar bleibt.

   FIX (Sticky-Flackern, v2): der Header wird nicht mehr per Opacity ein- und
        ausgeblendet und wechselt auch nicht mehr zwischen sticky und statisch.
        Er bleibt sticky und wird per transform aus dem Viewport geschoben
        (CSS: .c-sticky-host in custom-header.css). Dazu:
          - rAF-Throttle, maximal eine Auswertung pro Frame
          - Richtungs-Hysterese: Zustandswechsel erst nach zusammenhaengendem
            Scrollweg (hoch 40px, runter 20px), Momentum-Zittern laeuft ins Leere
          - setState() fasst Klassen nur bei echtem Zustandswechsel an
          - eingefroren, solange Drawer, Suche oder Mega-Menu offen sind
   ============================================================================
*/

(function () {
  'use strict';

  /* ============================================================================
     STICKY HEADER (clone of theme's StickyHeader, kept self-contained)
     ============================================================================
  */
  if (!customElements.get('sticky-header')) {
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // The actual <section class="section-header"> wrapper that Shopify
        // emits (section.tag = "section", section.class = "section-header").
        // We climb to it; if we don't find it we fall back to this element.
        this.header = this.closest('.section-header') || this;
        this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always'
                                 || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
        this.headerBounds = {};

        this.setHeaderHeight();

        const md = parseFloat(this.getAttribute('data-margin-desktop')) || 2;
        const mm = parseFloat(this.getAttribute('data-margin-mobile'))  || 2;
        this.marginDesktopThreshold = (md * 2) / 100;
        this.marginMobileThreshold  = mm / 300;

        this._mq = window.matchMedia('(max-width: 990px)');
        this._mqHandler = this.setHeaderHeight.bind(this);
        if (this._mq.addEventListener) this._mq.addEventListener('change', this._mqHandler);
        else this._mq.addListener(this._mqHandler);

        if (this.headerIsAlwaysSticky) {
          this.header.classList.add('shopify-section-header-sticky');
        }

        // Marker fuer die flackerfreien CSS-Regeln in custom-header.css.
        // Nur dieser Header bekommt die Klasse, das Original-Theme bleibt unberuehrt.
        this.header.classList.add('c-sticky-host');

        this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        this.preventReveal = false;

        // Zustandsmaschine + Richtungs-Hysterese
        this._state = 'reset';     // reset | reveal | hide
        this._lastDir = 0;         // 1 = runter, -1 = hoch
        this._dirAccum = 0;        // zurueckgelegter Weg in aktueller Richtung
        this._ticking = false;

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });

        this.createObserver();

        // also add the in-view class via a dedicated observer for the
        // floating/transparent CSS to kick in (matches original behavior).
        this.initInView();
      }

      disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
        if (this._mq && this._mq.removeEventListener) this._mq.removeEventListener('change', this._mqHandler);
        else if (this._mq) this._mq.removeListener(this._mqHandler);
      }

      setHeaderHeight() {
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      }

      createObserver() {
        const observer = new IntersectionObserver((entries, obs) => {
          this.headerBounds = entries[0].intersectionRect;
          obs.disconnect();
        });
        observer.observe(this.header);
      }

      initInView() {
        // The original adds `in-view` to .enable-fixed-header-part--true.enable-header-full-width--false
        // when fully visible. Replicate by observing this element.
        if (!this.classList.contains('enable-fixed-header-part--true')) return;
        if (this.classList.contains('enable-header-full-width--true')) return;

        const obs = new IntersectionObserver((entries, o) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in-view');
              o.unobserve(entry.target);
            }
          });
        }, { threshold: 1 });
        obs.observe(this);
      }

      onScroll() {
        // rAF-Throttle: pro Frame maximal eine Auswertung, verhindert
        // mehrfaches Umschalten innerhalb eines einzigen Repaints.
        if (this._ticking) return;
        this._ticking = true;
        requestAnimationFrame(() => {
          this._ticking = false;
          this.evaluateScroll();
        });
      }

      evaluateScroll() {
        if (!this.headerBounds || typeof this.headerBounds.bottom !== 'number') return;

        // Waehrend Drawer / Suche / Mega-Menu offen sind wird der Zustand
        // eingefroren (sonst wandert fixed positionierter Inhalt mit).
        if (document.body.classList.contains('c-mobile-drawer-open')) return;
        if (this.header.querySelector('.c-header.is-search-open')) return;
        if (this.header.querySelector('.c-mega.is-open')) return;

        const scrollTop = Math.max(0, window.pageYOffset || document.documentElement.scrollTop || 0);
        const scrollThreshold = 250;
        const viewportWidth = window.innerWidth;
        const resetOffset = viewportWidth < 990
          ? window.innerHeight * this.marginMobileThreshold
          : window.innerHeight * this.marginDesktopThreshold;

        const stickyThreshold = this.headerBounds.bottom + scrollThreshold;
        const resetThreshold  = this.headerBounds.top + resetOffset;

        const delta = scrollTop - this.currentScrollTop;
        this.currentScrollTop = scrollTop;
        if (delta === 0) return;

        // Ganz oben: immer zurueck in den Normalzustand.
        if (scrollTop <= resetThreshold) {
          this._lastDir = 0;
          this._dirAccum = 0;
          this.setState('reset');
          return;
        }

        // Richtungswechsel setzt den Zaehler zurueck.
        const dir = delta > 0 ? 1 : -1;
        if (dir !== this._lastDir) {
          this._lastDir = dir;
          this._dirAccum = 0;
        }
        this._dirAccum += Math.abs(delta);

        // Totzone zwischen reset- und sticky-Schwelle: Zustand bleibt.
        if (scrollTop <= stickyThreshold) return;

        // Hysterese: erst nach zusammenhaengendem Weg in eine Richtung wird
        // umgeschaltet. Hochscrollen braucht mehr Weg, weil dort das
        // Trackpad-/Momentum-Zittern am staerksten ist.
        const needed = dir < 0 ? 40 : 20;
        if (this._dirAccum < needed) return;

        if (dir > 0) {
          this.setState('hide');
          return;
        }

        if (this.preventReveal) {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => { this.preventReveal = false; }, 66);
          this.setState('hide');
          return;
        }

        this.setState('reveal');
      }

      // Klassen werden nur angefasst, wenn sich der Zustand wirklich aendert.
      setState(next) {
        if (this._state === next) return;
        this._state = next;

        const h = this.header;

        if (next === 'reset') {
          h.classList.remove('scrolled-past-header');
          if (!this.headerIsAlwaysSticky) {
            h.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky');
          }
          return;
        }

        h.classList.add('scrolled-past-header');
        if (this.headerIsAlwaysSticky) return;

        // sticky bleibt in BEIDEN Zustaenden gesetzt: der Header wird per
        // transform aus dem Viewport geschoben statt aus- und wieder
        // eingeblendet. Kein Positionswechsel, kein Opacity-Blitzen.
        h.classList.add('shopify-section-header-sticky');
        if (next === 'hide') {
          h.classList.add('shopify-section-header-hidden');
        } else {
          h.classList.remove('shopify-section-header-hidden');
        }
      }

      hide()   { this.setState('hide'); }
      reveal() { this.setState('reveal'); }
      reset()  { this.setState('reset'); }
    }

    customElements.define('sticky-header', StickyHeader);
  }

  /* ============================================================================
     C-HEADER (mega menu + mobile drawer)
     ============================================================================
  */
  class CHeader extends HTMLElement {
    constructor() {
      super();
      this.openTimer = null;
      this.closeTimer = null;
      this.activePanel = null;
    }

    connectedCallback() {
      this.bindMega();
      this.bindMobileDrawer();
      this.bindEscape();
      this.bindSearchSync();
    }

    /* ---------- MEGA MENU ---------- */
    bindMega() {
      const items = this.querySelectorAll('[data-mega]');
      const panels = this.querySelectorAll('[data-mega-panel]');
      if (!items.length || !panels.length) return;

      items.forEach((item) => {
        const key = item.getAttribute('data-mega');
        const panel = this.querySelector(`[data-mega-panel="${key}"]`);
        if (!panel) return;

        item.addEventListener('mouseenter', () => this.openMega(panel));
        item.addEventListener('mouseleave', () => this.scheduleCloseMega(panel));
        item.addEventListener('focusin',    () => this.openMega(panel));
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget) && !panel.contains(e.relatedTarget)) {
            this.scheduleCloseMega(panel);
          }
        });

        panel.addEventListener('mouseenter', () => this.cancelClose());
        panel.addEventListener('mouseleave', () => this.scheduleCloseMega(panel));
        panel.addEventListener('focusin',    () => this.cancelClose());
        panel.addEventListener('focusout', (e) => {
          if (!panel.contains(e.relatedTarget) && !item.contains(e.relatedTarget)) {
            this.scheduleCloseMega(panel);
          }
        });
      });
    }

    openMega(panel) {
      this.cancelClose();
      if (this.activePanel && this.activePanel !== panel) this.closeMega(this.activePanel);
      panel.hidden = false;
      panel.style.pointerEvents = '';   // reset, falls vorher von closeMega gesetzt
      // eslint-disable-next-line no-unused-expressions
      panel.offsetHeight;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      this.activePanel = panel;
    }

    scheduleCloseMega(panel) {
      this.cancelClose();
      this.closeTimer = setTimeout(() => this.closeMega(panel), 140);
    }

    closeMega(panel) {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      // FIX: pointer-events sofort entfernen, sonst blockt das schliessende
      // Panel (inkl. seiner ::before Hover-Bridge) den Hover des anderen.
      panel.style.pointerEvents = 'none';

      if (this.activePanel === panel) this.activePanel = null;

      const onEnd = (ev) => {
        if (ev.propertyName !== 'opacity') return;
        if (!panel.classList.contains('is-open')) {
          panel.hidden = true;
          panel.style.pointerEvents = '';
        }
        panel.removeEventListener('transitionend', onEnd);
      };
      panel.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!panel.classList.contains('is-open')) {
          panel.hidden = true;
          panel.style.pointerEvents = '';
        }
      }, 400);
    }

    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    /* ---------- MOBILE DRAWER ---------- */
    bindMobileDrawer() {
      const burger = this.querySelector('[data-c-burger]');
      const drawer = this.querySelector('[data-c-mobile-drawer]');
      const closers = this.querySelectorAll('[data-c-mobile-close]');
      if (!burger || !drawer) return;

      this.drawer = drawer;
      this.burger = burger;

      burger.addEventListener('click', () => this.toggleDrawer());
      closers.forEach((el) => el.addEventListener('click', () => this.closeDrawer()));

      drawer.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (link) this.closeDrawer();
      });
    }

    toggleDrawer() {
      this.drawer.classList.contains('is-open') ? this.closeDrawer() : this.openDrawer();
    }

    openDrawer() {
      this.drawer.hidden = false;
      // eslint-disable-next-line no-unused-expressions
      this.drawer.offsetHeight;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.burger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('c-mobile-drawer-open');
    }

    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.burger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('c-mobile-drawer-open');

      const onEnd = (ev) => {
        if (ev.propertyName !== 'transform') return;
        if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
        this.drawer.removeEventListener('transitionend', onEnd);
      };
      this.drawer.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
      }, 400);
    }

    /* ---------- ESCAPE ---------- */
    bindEscape() {
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (this.activePanel) this.closeMega(this.activePanel);
        if (this.drawer && this.drawer.classList.contains('is-open')) this.closeDrawer();
        if (this._closeSearch) {
          const headerEl = this.querySelector('.c-header');
          if (headerEl && headerEl.classList.contains('is-search-open')) this._closeSearch();
        }
      });
    }

    /* ---------- SEARCH ↔ MEGA SYNC ----------
       The details element ([data-c-search]) is just the toggle button.
       The actual search overlay lives as a sibling inside .c-header.
       When details opens, we add .is-search-open to .c-header. */
    bindSearchSync() {
      const details = this.querySelector('[data-c-search]');
      if (!details) return;

      const headerEl = this.querySelector('.c-header');
      const overlay = this.querySelector('[data-c-search-overlay]');
      const backdrop = this.querySelector('[data-c-search-backdrop]');
      const input = this.querySelector('.c-search-form__input');
      const closeBtn = this.querySelector('[data-c-search-close]');

      if (!headerEl || !overlay) return;

      // Prevent the default details toggle so we control timing
      const summary = details.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          if (details.open) {
            this._closeSearch();
          } else {
            details.open = true;
            this._openSearch();
          }
        });
      }

      this._openSearch = () => {
        if (this.activePanel) this.closeMega(this.activePanel);
        details.open = true;
        headerEl.classList.add('is-search-open');
        overlay.hidden = false;
        overlay.setAttribute('aria-hidden', 'false');
        if (backdrop) backdrop.setAttribute('aria-hidden', 'false');
        if (input) requestAnimationFrame(() => input.focus());
      };

      this._closeSearch = () => {
        details.open = false;
        headerEl.classList.remove('is-search-open');
        overlay.setAttribute('aria-hidden', 'true');
        if (backdrop) backdrop.setAttribute('aria-hidden', 'true');
        setTimeout(() => {
          if (!headerEl.classList.contains('is-search-open')) overlay.hidden = true;
        }, 260);
      };

      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this._closeSearch();
        });
      }

      if (backdrop) {
        backdrop.addEventListener('click', () => this._closeSearch());
      }

      // Close search if clicking outside
      document.addEventListener('click', (e) => {
        if (!details.open) return;
        if (headerEl.contains(e.target)) return;
        this._closeSearch();
      });
    }
  }

  if (!customElements.get('c-header')) {
    customElements.define('c-header', CHeader);
  }
})();